import React, { useState } from 'react';
import { Mail, Send, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import apiClient from '../../services/api/client';

const Contact: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [contactRef, contactVisible] = useScrollAnimation();
  const [learnMoreExpanded, setLearnMoreExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Prepare the request payload
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message
      };

      // Call the API endpoint
      const response = await apiClient.post('/api/contact/create-submission', payload);

      // Check if the response is successful
      if (response.data.success) {
        // Show success toast notification
        toast.success(response.data.message || 'Thank you for contacting us! We will get back to you soon.', {
          duration: 5000,
          style: {
            background: '#10B981',
            color: '#FFFFFF',
            padding: '16px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
          },
          iconTheme: {
            primary: '#FFFFFF',
            secondary: '#10B981',
          },
        });

        // Reset form
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error(response.data.message || 'Failed to submit contact form');
      }
    } catch (error: any) {
      console.error('Error submitting contact form:', error);

      // Extract error message
      const errorMessage = error.response?.data?.message
        || error.userMessage
        || error.message
        || 'Failed to submit your message. Please try again later.';

      // Show error toast notification
      toast.error(errorMessage, {
        duration: 5000,
        style: {
          background: '#EF4444',
          color: '#FFFFFF',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#FFFFFF',
          secondary: '#EF4444',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // For phone field, only allow numbers, plus, minus, and spaces
    if (name === 'phone') {
      const filteredValue = value.replace(/[^0-9+\- ]/g, '');
      setFormData({
        ...formData,
        [name]: filteredValue
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 md:pb-32 px-4 bg-mesh-premium relative overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0c513f]/5 rounded-full blur-[100px] animate-blob"></div>
          <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-chowdeck-pastelYellow/20 rounded-full blur-[120px] animate-blob animation-delay-2000"></div>
        </div>

        <div className={`container-premium text-center transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
          }`}>
          <h1 className="heading-huge text-[#0c513f] mb-8 tracking-tighter-premium">
            Get In Touch
          </h1>
          <p className="text-2xl md:text-3xl text-secondary-900 max-w-3xl mx-auto leading-tight font-medium tracking-tight">
            Have questions? We're here to help. Reach out to us and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} className="py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <div className={`transition-all duration-700 ${contactVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
            }`}>
            {/* Contact Info Header - Centered */}
            <div className="text-center mb-10">
              <h2 className="text-4xl font-black text-[#0c513f] mb-4 tracking-tight">
                Contact Information
              </h2>
              <p className="text-xl text-secondary-600 mb-8 font-medium">
                Fill out the form and our team will get back to you within 24 hours.
              </p>
              <div className="inline-flex items-center space-x-3 bg-white px-8 py-4 rounded-full border border-secondary-100 shadow-lg hover:scale-105 transition-transform">
                <Mail className="text-[#0c513f]" size={24} />
                <span className="text-[#0c513f] font-bold text-lg">info@rita.ng</span>
              </div>
            </div>

            {/* Contact Form - Centered and More Compact */}
            <div className="max-w-2xl mx-auto bg-white p-10 md:p-14 rounded-[3rem] shadow-2xl border border-[#0c513f]/5">
              <h3 className="text-3xl font-black text-[#0c513f] mb-10 text-center tracking-tight">
                Send Us a Message
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                    placeholder="+1 234 567 890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="business">Business Registration</option>
                    <option value="support">Technical Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 disabled:cursor-not-allowed text-white py-4 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all hover:scale-105 shadow-green disabled:hover:scale-100"
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  {!isSubmitting && <Send size={20} />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More About Rita - SEO Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setLearnMoreExpanded(!learnMoreExpanded)}
              className="bg-secondary-200 hover:bg-secondary-300 text-secondary-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md"
            >
              <span>Learn More About Rita</span>
              {learnMoreExpanded ? (
                <ChevronUp size={20} className="transition-transform" />
              ) : (
                <ChevronDown size={20} className="transition-transform" />
              )}
            </button>
          </div>

          <div
            className={`overflow-hidden transition-all duration-500 ${learnMoreExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <div className="max-w-4xl mx-auto space-y-8 text-secondary-700">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                  Rita - A Universal QR Payment & Discovery Platform
                </h2>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    Rita is a comprehensive digital platform that revolutionizes the way customers discover businesses and make payments.
                    Our universal QR payment and discovery platform offers seamless digital wallet solutions, instant QR code payments,
                    business location services, and real-time navigation. Whether you're looking for nearby stores, restaurants, salons,
                    fuel stations, or service providers, Rita simplifies your journey by combining business discovery, navigation, and
                    payment processing into one intuitive platform.
                  </p>
                  <p>
                    Our mission is to empower businesses of all sizes while providing customers with a smooth, convenient experience.
                    For consumers, Rita offers a seamless way to discover and pay at local businesses without the need for cash or cards.
                    For business owners, we provide cutting-edge point-of-sale systems, comprehensive analytics, and intelligent payment
                    solutions that help grow their customer base and streamline operations. Rita delivers convenience, transparency, and
                    cutting-edge technology through our advanced business management tools, dedicated mobile applications, and secure
                    payment infrastructure.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Key Features of Rita
                </h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Digital Wallet
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's secure and intuitive digital wallet allows users to hold funds, make instant payments, and track all
                      transactions in real-time. With our wallet, you can easily manage your finances, view transaction history,
                      and enjoy the convenience of scanning QR codes instead of carrying cash or cards. The wallet is protected
                      by advanced encryption and security measures to ensure your money and personal information remain safe.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      QR Code Payment System
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Our innovative QR code payment system enables instant, contactless transactions at any business partner.
                      Simply scan the QR code displayed at the business location, confirm the amount, and complete your payment
                      securely through your digital wallet. This eliminates the need for physical cash or credit cards, making
                      transactions faster, safer, and more convenient for both customers and businesses.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Discovery & Navigation
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's powerful discovery platform helps you find nearby businesses based on your location, preferences,
                      and search criteria. Our integrated mapping system provides real-time navigation, directions, and business
                      information including hours, ratings, and available services. Whether you're looking for a specific store,
                      restaurant, or service provider, Rita makes it easy to discover and navigate to your destination.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Analytics & Management
                    </h4>
                    <p className="text-lg leading-relaxed">
                      For business owners, Rita offers comprehensive analytics and management tools to track performance, monitor
                      transactions, and gain valuable insights into customer behavior. Our dashboard provides real-time reports,
                      sales analytics, customer demographics, and performance metrics to help you make data-driven decisions and
                      grow your business effectively.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Security & Verification
                    </h4>
                    <p className="text-lg leading-relaxed">
                      All businesses on the Rita platform undergo KYC (Know Your Customer) verification to ensure authenticity
                      and security. Our platform uses industry-standard encryption protocols to protect all transactions and
                      personal data. With Rita, you can trust that every transaction is secure, verified, and protected by
                      advanced security measures.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Multi-Category Support
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita supports businesses across multiple categories including retail stores, restaurants, cafes, salons,
                      fuel stations, service providers, healthcare facilities, and more. Our platform is designed to accommodate
                      various business types and industries, making it a versatile solution for businesses of all sizes and sectors.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  How Rita Benefits You
                </h3>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    <strong className="text-secondary-900">For Customers:</strong> Rita provides a seamless, cashless payment
                    experience with the convenience of discovering and navigating to nearby businesses. Our platform saves time,
                    reduces the need for physical currency, and offers a secure way to manage your transactions and explore local
                    businesses.
                  </p>
                  <p>
                    <strong className="text-secondary-900">For Businesses:</strong> Rita helps businesses reach more customers,
                    streamline payment processing, and gain valuable insights through our analytics platform. Our tools enable
                    businesses to accept digital payments easily, track performance, and grow their customer base with minimal
                    setup and maintenance.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Getting Started with Rita
                </h3>
                <p className="text-lg leading-relaxed">
                  Getting started with Rita is simple and straightforward. Download our mobile application, create your account,
                  and set up your digital wallet. For businesses, our onboarding process includes KYC verification, business profile
                  setup, and QR code generation. Our support team is available to assist you throughout the process, ensuring a
                  smooth and hassle-free experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;

